import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Row, Col, Button, Container, FormInput, Badge } from 'shards-react';
import PageTitle from '../../../components/PageTitle';
import Spinner from '../../../components/common/GenericSearchBar/Spinner';
import { useDocumentTrace } from '../../../hooks/queries/useDocumentTrace';
import { useUrlState } from '../../../hooks/useUrlState';
import { stringParam } from '../../../utils/urlStateParams';

const stageStyle = {
  borderLeft: '3px solid #007bff',
  paddingLeft: '18px',
  marginBottom: '28px'
};

const monoStyle = {
  fontFamily: 'monospace',
  fontSize: '12px',
  overflowWrap: 'anywhere'
};

const tableStyle = {
  width: '100%',
  background: '#fff',
  borderCollapse: 'collapse'
};

const cellStyle = {
  borderBottom: '1px solid #e1e5eb',
  padding: '8px',
  verticalAlign: 'top'
};

const headerCellStyle = {
  ...cellStyle,
  fontWeight: 600,
  background: '#f8f9fa'
};

const emptyStyle = {
  color: '#6c757d',
  fontStyle: 'italic'
};

const timestampStyle = {
  fontFamily: 'monospace',
  fontSize: '14px'
};

const safeJson = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

const formatJson = (raw) => {
  const parsed = safeJson(raw);
  if (!parsed) return '-';
  if (typeof parsed === 'string') return parsed;
  return JSON.stringify(parsed, null, 2);
};

const documentFileName = (document) => {
  const ref = document.documentStorageRef || '';
  return ref.split('/').pop() || document.id;
};

const statusBadge = (label, theme = 'secondary') => (
  <Badge pill theme={theme}>
    {label}
  </Badge>
);

const processedParts = (trace) => {
  const status = safeJson(trace.scraperDocuments[0]?.processStatus);
  return Array.isArray(status?.processed_parts) ? status.processed_parts : [];
};

const processedPartResults = (trace) => {
  const status = safeJson(trace.scraperDocuments[0]?.processStatus);
  return status?.processed_part_results || {};
};

const apiResult = (partResult) => partResult?.api_result || partResult?.apiResult;

const existingPaymentId = (partResult) =>
  partResult?.existing_payment_id || partResult?.existingPaymentId;

const expectedTotal = (trace) => {
  const status = safeJson(trace.scraperDocuments[0]?.processStatus);
  return status?.expected_total || null;
};

const paymentsByParsedId = (payments) =>
  payments.reduce((memo, payment) => {
    const key = payment.parsedDocumentExtId || 'sin parsed id';
    memo[key] = [...(memo[key] || []), payment];
    return memo;
  }, {});

const failsByParsedId = (fails) =>
  fails.reduce((memo, fail) => {
    const key = fail.parsedDocumentExtId || 'sin parsed id';
    memo[key] = [...(memo[key] || []), fail];
    return memo;
  }, {});

const splitDiagnosis = (parserDocument, processed, payments, fails, partResult) => {
  if (fails.length) return statusBadge('API fail', 'danger');
  if (apiResult(partResult) === 'already_processed') {
    return statusBadge('ya procesado en API', 'info');
  }
  if (payments.length) return statusBadge('payment creado', 'success');
  if (processed.includes(parserDocument.id)) return statusBadge('callback sin payment', 'warning');
  return statusBadge('sin callback API', 'warning');
};

const Stage = ({ title, children }) => (
  <div style={stageStyle}>
    <h5 className="mb-3">{title}</h5>
    {children}
  </div>
);

const Empty = ({ children }) => <p style={emptyStyle}>{children}</p>;

const ScraperStage = ({ documents }) => (
  <Stage title="1. Scraper">
    {!documents.length ? (
      <Empty>No se encontró documento en scraper.</Empty>
    ) : (
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={headerCellStyle}>Documento</th>
            <th style={headerCellStyle}>Estado</th>
            <th style={headerCellStyle}>Archivo</th>
            <th style={headerCellStyle}>Process status</th>
          </tr>
        </thead>
        <tbody>
          {documents.map((document) => (
            <tr key={document.id}>
              <td style={cellStyle}>
                <div style={monoStyle}>{document.id}</div>
                <div>
                  Cuenta: <span style={monoStyle}>{document.accountId}</span>
                </div>
                <div>
                  Relacionado: <span style={monoStyle}>{document.relatedDocumentId || '-'}</span>
                </div>
                <div>Transformación: {String(document.requiresTransformation)}</div>
              </td>
              <td style={cellStyle}>
                <div>parsed: {String(document.parsed)}</div>
                <div>processInApi: {String(document.processInApi)}</div>
                <div>status: {document.status}</div>
                <div>
                  authorizedDocumentTypeId:{' '}
                  <span style={monoStyle}>{document.authorizedDocumentTypeId || '-'}</span>
                </div>
              </td>
              <td style={cellStyle}>
                <a href={document.link} rel="noopener noreferrer" target="_blank">
                  {documentFileName(document)}
                </a>
                <div style={monoStyle}>{document.documentStorageRef}</div>
              </td>
              <td style={cellStyle}>
                <pre style={monoStyle}>{formatJson(document.processStatus)}</pre>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </Stage>
);

const ParserStage = ({ documents, fails }) => (
  <Stage title="2. Parser">
    {!documents.length && !fails.length ? (
      <Empty>No se encontraron documentos ni errores de parser.</Empty>
    ) : (
      <>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={headerCellStyle}>Parsed doc</th>
              <th style={headerCellStyle}>Split</th>
              <th style={headerCellStyle}>Parser</th>
              <th style={headerCellStyle}>Archivo .increase</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((document) => (
              <tr key={document.id}>
                <td style={cellStyle}>
                  <div style={monoStyle}>{document.id}</div>
                  <div>
                    scraped: <span style={monoStyle}>{document.scrapedDocumentId}</span>
                  </div>
                </td>
                <td style={cellStyle}>{document.split}</td>
                <td style={cellStyle}>
                  <div>
                    {document.infoProvider} / {document.documentType}
                  </div>
                  <div>
                    v{document.majorVersion}.{document.minorVersion}.{document.patchVersion}
                  </div>
                  <div>lineCount: {document.lineCount || '-'}</div>
                </td>
                <td style={cellStyle}>
                  <div style={monoStyle}>{document.parsedDocumentStorageRef}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {fails.length > 0 && (
          <>
            <h6 className="mt-4">Parser fails</h6>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>Fail</th>
                  <th style={headerCellStyle}>Error</th>
                  <th style={headerCellStyle}>Message</th>
                </tr>
              </thead>
              <tbody>
                {fails.map((fail) => (
                  <tr key={fail.id}>
                    <td style={cellStyle}>
                      <div style={monoStyle}>{fail.id}</div>
                      <div>{fail.createdAt}</div>
                    </td>
                    <td style={cellStyle}>
                      <div>{fail.exceptionClass || '-'}</div>
                      <div>{fail.debugMessage || '-'}</div>
                      <div>line: {fail.line || '-'}</div>
                    </td>
                    <td style={cellStyle}>
                      <pre style={monoStyle}>{formatJson(fail.message)}</pre>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </>
    )}
  </Stage>
);

const ApiStage = ({ trace }) => {
  const processed = processedParts(trace);
  const partResults = processedPartResults(trace);
  const byPayment = paymentsByParsedId(trace.payments);
  const byFail = failsByParsedId(trace.paymentFails);
  const parserDocs = trace.parserDocuments;

  return (
    <Stage title="3. API">
      {parserDocs.length ? (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={headerCellStyle}>Parsed doc</th>
              <th style={headerCellStyle}>Callback</th>
              <th style={headerCellStyle}>Payments</th>
              <th style={headerCellStyle}>Payment fails</th>
              <th style={headerCellStyle}>Diagnóstico</th>
            </tr>
          </thead>
          <tbody>
            {parserDocs.map((document) => {
              const payments = byPayment[document.id] || [];
              const fails = byFail[document.id] || [];
              const partResult = partResults[document.id] || {};
              const existingPayment = existingPaymentId(partResult);
              return (
                <tr key={document.id}>
                  <td style={cellStyle}>
                    <div style={monoStyle}>{document.id}</div>
                    <div>split: {document.split}</div>
                  </td>
                  <td style={cellStyle}>
                    {processed.includes(document.id)
                      ? statusBadge('recibido', 'success')
                      : statusBadge('faltante', 'warning')}
                  </td>
                  <td style={cellStyle}>
                    {payments.length || 0}
                    {payments.map((payment) => (
                      <div key={payment.id} style={monoStyle}>
                        {payment.id}
                      </div>
                    ))}
                    {existingPayment && (
                      <div>
                        existente: <span style={monoStyle}>{existingPayment}</span>
                      </div>
                    )}
                  </td>
                  <td style={cellStyle}>
                    {fails.length || 0}
                    {fails.map((fail) => (
                      <div key={fail.id}>
                        <div style={monoStyle}>{fail.id}</div>
                        <div>{fail.debugMessage || fail.moreInfo || '-'}</div>
                      </div>
                    ))}
                  </td>
                  <td style={cellStyle}>
                    {splitDiagnosis(document, processed, payments, fails, partResult)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <Empty>No hay parsed docs para cruzar contra API.</Empty>
      )}
      <h6 className="mt-4">Payments encontrados</h6>
      {trace.payments.length ? (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={headerCellStyle}>Payment</th>
              <th style={headerCellStyle}>Parsed doc</th>
              <th style={headerCellStyle}>Info</th>
            </tr>
          </thead>
          <tbody>
            {trace.payments.map((payment) => (
              <tr key={payment.id}>
                <td style={cellStyle}>
                  <span style={monoStyle}>{payment.id}</span>
                </td>
                <td style={cellStyle}>
                  <span style={monoStyle}>{payment.parsedDocumentExtId}</span>
                </td>
                <td style={cellStyle}>
                  <div>
                    {payment.infoProvider} / {payment.documentType}
                  </div>
                  <div>paymentDate: {payment.paymentDate}</div>
                  <div>focusedForApi: {String(payment.focusedForApi)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <Empty>No se encontraron payments.</Empty>
      )}
    </Stage>
  );
};

const URL_STATE_CONFIG = {
  documentId: stringParam()
};

export function SupportDocumentTrace() {
  const [urlState, setUrlState] = useUrlState(URL_STATE_CONFIG);
  const documentId = urlState.documentId;
  const [draft, setDraft] = useState(documentId);
  const { trace, loading, error, fetchedAt, refetch } = useDocumentTrace(documentId);

  const processedCount = useMemo(() => (trace ? processedParts(trace).length : 0), [trace]);
  const total = useMemo(() => (trace ? expectedTotal(trace) : null), [trace]);

  // Keep the input in sync when the URL changes from outside (deeplink, back/forward).
  useEffect(() => {
    setDraft(documentId);
  }, [documentId]);

  const handleSearch = () => {
    const nextDocumentId = draft.trim();
    if (!nextDocumentId) return;

    if (nextDocumentId === documentId) {
      refetch();
      return;
    }

    setUrlState({ documentId: nextDocumentId });
  };

  const handleChange = (event) => {
    setDraft(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    handleSearch();
  };

  return (
    <Container className="main-content-container px-4" fluid>
      <Row className="page-header py-4" noGutters>
        <PageTitle className="text-sm-left" sm="4" subtitle="" title="Document Trace" />
      </Row>
      <Row className="mb-4">
        <Col lg="8" sm="12" xs="12">
          <FormInput
            id="documentTraceId"
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Scraper document id, parser document id o payment id"
            type="text"
            value={draft}
          />
        </Col>
        <Col lg="4" sm="12" xs="12">
          <Button disabled={!draft.trim() || loading} onClick={handleSearch}>
            {loading ? 'Buscando...' : 'Buscar traza'}
          </Button>
        </Col>
      </Row>
      {error && <p className="text-danger">No se pudo buscar la traza del documento.</p>}
      {!trace && loading && documentId && (
        <Row className="mb-4">
          <Col>
            <div className="d-flex align-items-center">
              <Spinner />
              <span className="ml-2">Cargando traza de {documentId}...</span>
            </div>
          </Col>
        </Row>
      )}
      {trace && (
        <>
          {fetchedAt && (
            <div className="mb-3" style={{ fontSize: '14px' }}>
              <span className="text-muted">Actualizado:</span>{' '}
              <strong style={timestampStyle}>{format(fetchedAt, 'dd/MM HH:mm:ss')}</strong>
            </div>
          )}
          <Row className="mb-4">
            <Col>
              <div>
                <strong>Scraped document:</strong>{' '}
                <span style={monoStyle}>{trace.scrapedDocumentId || '-'}</span>
              </div>
              <div>
                <strong>Progreso API:</strong>{' '}
                {total ? `${processedCount}/${total}` : `${processedCount}/?`}
              </div>
              <div>
                <strong>Parser docs:</strong> {trace.parserDocuments.length} ·{' '}
                <strong>Payments:</strong> {trace.payments.length} · <strong>Payment fails:</strong>{' '}
                {trace.paymentFails.length} · <strong>Parser fails:</strong>{' '}
                {trace.parserFails.length}
              </div>
            </Col>
          </Row>
          {!trace.found && <Empty>No se encontró información para ese id.</Empty>}
          <ScraperStage documents={trace.scraperDocuments} />
          <ParserStage documents={trace.parserDocuments} fails={trace.parserFails} />
          <ApiStage trace={trace} />
        </>
      )}
    </Container>
  );
}
